/*
 * The details sheet on the Promotions page (board SF 05b; GUARDRAILS G9):
 * its status panel, rows and terms, all written from the offer's settings,
 * never typed. The terms say only what the server enforces (mpgadmin G45,
 * D30); the board's lines that don't hold here are left out
 * (.claude/tasks/promotions-details.md, and G9). Pure: no clock, no screen.
 */
import type { Promotion } from '../types/promotion.types';
import { dayText, listText, money, offText, ordinal, summaryLine } from './promotionTerms';

export interface DetailRow {
  label: string;
  value: string;
}

const AUDIENCE = ['Everyone', 'New clients', 'Returning clients'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "2026-10-31" → "Sat 31 Oct 2026". The day of the week from the date alone, never the clock. */
export const endText = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return dayText(iso.slice(0, 10));
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${dayText(iso.slice(0, 10))}`;
};

/** The line under the sheet's title (SF 05b): the offer's own name, or its short terms. */
export const detailSub = (p: Promotion): string =>
  p.caption?.trim() || (p.isServiceDeal && p.headline?.trim()) || summaryLine(p);

export interface DetailStatus {
  /** "Claimed", "Yours", "No claim needed", "Not claimed yet". */
  title: string;
  line: string;
  /** The line is why it wouldn't apply today ("All 3 used"): drawn amber. */
  why: boolean;
}

/**
 * The panel under the title (SF 05b): where the offer stands for this
 * client. "Used by itself" is what the server does with an offer that is
 * theirs (mpgadmin G45); a server reason ("All 3 used") wins the line.
 */
export const detailStatus = (p: Promotion): DetailStatus => {
  const fits = 'Used by itself on your next booking that fits.';
  if (p.yours) {
    const title = p.claimed ? 'Claimed' : 'Yours';
    if (p.reason) return { title, line: p.reason.trim(), why: true };
    return { title, line: p.claimed ? fits : `No claim needed. ${fits}`, why: false };
  }
  if (p.noClaimNeeded) return { title: 'No claim needed', line: 'Used by itself on a booking it fits.', why: false };
  return { title: 'Not claimed yet', line: 'Claim it, and it’s used by itself on your next booking that fits.', why: false };
};

export const detailRows = (p: Promotion): DetailRow[] => {
  // A deal on services (mpgadmin G138): the server's words for what it saves and on what.
  const deal = !!p.isServiceDeal;
  const rows: DetailRow[] = [
    { label: 'You save', value: deal && p.headline ? p.headline : `${offText(p)} what you pay at the salon` },
  ];
  // A whole-booking promotion is worked out on the booking's services and products,
  // never on a package bought (mpgadmin G45); a deal names its services itself.
  rows.push({
    label: 'Applies to',
    value: deal ? (p.serviceNames?.length ? listText(p.serviceNames) : 'Any service') : 'Your whole booking, not packages',
  });
  if (deal && p.petCountLine) rows.push({ label: 'Pets', value: p.petCountLine });
  if (p.minSpend && p.minSpend > 0) rows.push({ label: 'Minimum spend', value: money(p.minSpend) });
  rows.push({ label: 'Who', value: AUDIENCE[p.audience] ?? AUDIENCE[0] });
  rows.push({
    label: 'Where',
    value: p.allLocations || !p.locationNames?.length ? 'All locations' : `${listText(p.locationNames)} only`,
  });
  if (p.rebookWithinWeeks) {
    rows.push({
      label: 'How often',
      value: p.rewardEveryNthVisit
        ? `Every ${ordinal(p.rewardEveryNthVisit)} visit in a row, each within ${p.rebookWithinWeeks} weeks of the last`
        : `Each visit booked within ${p.rebookWithinWeeks} weeks of the last groom`,
    });
  }
  const unit = p.rebookWithinWeeks ? (p.rewardEveryNthVisit ? 'reward' : 'visit') : 'booking';
  // No limit per client (mpgadmin D91): every qualifying one while the offer runs.
  const n = p.usesPerClient == null ? null : Math.max(1, p.usesPerClient);
  const times = n == null ? `No limit: every ${unit} while it runs` : `${n} ${unit}${n === 1 ? '' : 's'}`;
  rows.push({ label: 'How many times', value: p.yours && p.usesText ? `${times} · ${p.usesText}` : times });
  rows.push({ label: 'Ends', value: p.endsOn ? endText(p.endsOn) : 'No end date' });
  return rows;
};

/** What the server does with every promotion (mpgadmin G45, D30), and nothing it doesn't. */
export const detailTerms = (p: Promotion): string[] => {
  // A deal on services: the server's own sentences, then what holds for every offer.
  if (p.isServiceDeal && p.details)
    return [
      p.details,
      'Not on packages.',
      'A cancelled or missed booking gives its use back.',
      'The salon can pause or end an offer; a booking already made keeps it.',
    ];
  const terms = [
    'Comes off what you pay at the salon. Your deposit is the same with or without it.',
    p.yours || p.noClaimNeeded
      ? 'Used by itself when a booking fits; you can change it or take it off on Review and book.'
      : 'Once claimed, it’s used by itself when a booking fits; you can change it or take it off on Review and book.',
    p.combinesWithServiceDeals
      ? 'One promotion per booking, on top of any deals on its services, and not on packages.'
      : 'One promotion per booking, and not on packages.',
    'A cancelled or missed booking gives its use back.',
    'The salon can pause or end an offer; a booking already made keeps it.',
  ];
  if (p.rebookWithinWeeks && p.rewardEveryNthVisit) terms.splice(3, 0, 'A visit outside the window starts the count again.');
  else if (p.rebookWithinWeeks) terms.splice(3, 0, 'A visit outside the window is full price; the next one in time gets it again.');
  return terms;
};

/**
 * Why one of their promotions isn't on this booking, for the summary (Lena,
 * 5 Oct: "tell me this promo is not eligible yet"). The server's reason is
 * kept as it is; an every-Nth-visit count also says when it comes.
 */
export const notThisBooking = (reason?: string | null): string => {
  if (!reason) return "Not on this booking: it doesn't fit it.";
  const visit = /^Visit (\d+) of (\d+)$/i.exec(reason.trim());
  if (visit) return `Not yet: visit ${visit[1]} of ${visit[2]}. It comes off every ${ordinal(Number(visit[2]))} visit in a row.`;
  return `Not on this booking: ${reason.trim().replace(/\.$/, '')}.`;
};
